import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ExternalLink, MessageCircle, Printer, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { printEwayBills, printEwayExtensionLog } from "@/lib/print";
import { ListPagination } from "@/components/ListPagination";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const isDelivered = (r) =>
  String(r.status || "").toLowerCase() === "delivered" || !!r.pod_path || !!r.delivery_date;

export default function EWayBills() {
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    api.get("/shipments").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const data = useMemo(() => {
    const t = todayStr();
    const custName = Object.fromEntries(customers.map((c) => [c.id, c.name]));
    return (rows || [])
      .flatMap((r) => {
        const entries = [
          { no: String(r.eway_bill || "").trim(), valid: r.eway_valid || "" },
          ...(r.extra_eways || []).map((e) => ({ no: String(e.no || "").trim(), valid: e.valid || "" })),
          ...(r.extra_invoices || []).filter((x) => String(x.eway || "").trim()).map((x) => ({ no: String(x.eway).trim(), valid: x.eway_valid || "" })),
        ].filter((e) => e.no);
        return entries.map((e, i) => ({ ...r, eway_bill: e.no, eway_valid: e.valid, _sid: r.id, id: i ? `${r.id}-ew${i}` : r.id }));
      })
      .map((r) => {
        const delivered = isDelivered(r);
        const validUpto = r.eway_valid || "";
        const expired = !delivered && validUpto && validUpto < t;
        const autoExtended = expired ? addDays(t, 1) : "";
        const customer = custName[r.customer_id] || r.customer_name || r.consignor || "";
        return { ...r, _delivered: delivered, _validUpto: validUpto, _expired: expired, _autoExtended: autoExtended, _customer: customer };
      })
      .filter((r) => (!pendingOnly || !r._delivered))
      .filter((r) => !q || `${r.eway_bill} ${r.lr_no} ${r.consignment_no} ${r.consignor} ${r.consignee} ${r._customer}`.toLowerCase().includes(q.toLowerCase()));
  }, [rows, q, pendingOnly, customers]);

  const exportRows = sel.size ? data.filter((r) => sel.has(r.id)) : data;
  const [pageNo, setPageNo] = useState(1);
  const EWAY_PER_PAGE = 20;
  const ewayTotalPages = Math.max(1, Math.ceil(data.length / EWAY_PER_PAGE));
  const ewayPage = Math.min(pageNo, ewayTotalPages);
  const ewayOffset = (ewayPage - 1) * EWAY_PER_PAGE;
  const pagedData = data.slice(ewayOffset, ewayOffset + EWAY_PER_PAGE);
  const allSel = data.length > 0 && data.every((r) => sel.has(r.id));
  const toggleAllSel = () => setSel((p) => { const n = new Set(p); const all = data.every((r) => n.has(r.id)); data.forEach((r) => (all ? n.delete(r.id) : n.add(r.id))); return n; });

  const excel = () => {
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      [`E-Way Bill Register — as on ${fmtDate(todayStr())}`],
      ["Sl No", "E-Way Bill No.", "LR No.", "LR Date", "Customer Name", "From", "Destination", "Vehicle No.", "Vehicle Type", "Valid Upto", "Auto-extended Upto", "Delivered", "Status"],
      ...exportRows.map((r, i) => [i + 1, r.eway_bill, r.lr_no || r.consignment_no || "", r.lr_date ? fmtDate(r.lr_date) : "", r._customer, r.origin || "", r.destination || "", r.vehicle_no || "", r.vehicle_type || "", r._validUpto ? fmtDate(r._validUpto) : "", r._expired ? fmtDate(r._autoExtended) : "", r._delivered ? "Yes" : "No", r._delivered ? "Delivered - closed" : r._expired ? "Needs extension" : "Active"]),
    ];
    const blob = new Blob(["\ufeff" + lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `eway-bills-${todayStr()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const expiredCount = data.filter((r) => r._expired).length;
  const noVehicleCount = data.filter((r) => !r._delivered && !r.vehicle_no).length;
  const sendWhatsAppNudge = () => {
    const list = data.filter((r) => r._expired);
    const lines = list.map((r, i) => `${i + 1}. EWB ${r.eway_bill} — LR ${r.lr_no || r.consignment_no || "—"} — ${r.origin || ""} → ${r.destination || ""} — valid upto ${r._validUpto ? fmtDate(r._validUpto) : "—"}`);
    const msg = `⚠️ NTC E-Way Bill Extension Reminder (${fmtDate(new Date().toISOString().slice(0, 10))})\n\n${list.length} e-way bill(s) need extension on the GST portal today:\n\n${lines.join("\n")}\n\nPlease extend them at https://ewaybillgst.gov.in before expiry.\n\n— Nisha Transport Corporation`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div>
      <PageHeader
        title="E-Way Bills"
        subtitle="Track each consignment's e-way bill. Undelivered bills auto-roll a suggested valid-upto date and flag when they need extension on the GST portal."
        action={
          <div className="flex items-center gap-2">
            {expiredCount ? (
              <span className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700" data-testid="eway-expired-alert">
                <AlertTriangle className="h-4 w-4" /> {expiredCount} need extension on GST portal
              </span>
            ) : null}
            {noVehicleCount ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700" data-testid="eway-no-vehicle-count">
                <AlertTriangle className="h-4 w-4" /> {noVehicleCount} missing Vehicle No. for GST portal
              </span>
            ) : null}
            {expiredCount ? (
              <Button variant="outline" className="text-emerald-600" onClick={sendWhatsAppNudge} data-testid="eway-whatsapp-nudge-btn">
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Reminder
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => printEwayBills(exportRows)} data-testid="eway-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button variant="outline" onClick={excel} data-testid="eway-excel-btn">
              <FileDown className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const r = await api.get("/eway/extensions");
                  if (!r.data.extensions.length) {
                    toast.info("No e-way extensions recorded yet.");
                    return;
                  }
                  printEwayExtensionLog(r.data.extensions);
                } catch {
                  toast.error("Could not load extension log");
                }
              }}
              data-testid="eway-extension-log-btn"
            >
              <Printer className="mr-2 h-4 w-4" /> Extension Log
            </Button>
            <a href="https://ewaybillgst.gov.in/login.aspx" target="_blank" rel="noopener noreferrer" data-testid="eway-login-link">
              <Button variant="outline">
                <ExternalLink className="mr-2 h-4 w-4" /> E-Way Bill Login
              </Button>
            </a>
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input className="h-9 w-full max-w-xs text-sm" placeholder="Search e-way bill / LR / party" value={q} onChange={(e) => setQ(e.target.value)} data-testid="eway-search" />
        <div className="flex items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2">
          <Switch checked={pendingOnly} onCheckedChange={setPendingOnly} data-testid="eway-pending-only" />
          <Label className="text-xs font-medium text-slate-600">Undelivered only</Label>
        </div>
      </div>

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={toggleAllSel} data-testid="eway-select-all" /></TableHead>
              {["SL No.", "E-Way Bill No.", "LR No.", "LR Date", "Customer Name", "From", "Destination", "Vehicle No.", "Vehicle Type", "Valid Upto", "Auto-extended Upto", "Delivered", "Status"].map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-slate-500">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>{Array.from({ length: 13 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
              ))}
            {rows && data.length === 0 && (
              <TableRow><TableCell colSpan={14} className="py-12 text-center text-muted-foreground">No e-way bills entered yet. Add an E-Way Bill No. on a consignment.</TableCell></TableRow>
            )}
            {rows &&
              pagedData.map((r, i) => (
                <TableRow key={r.id} data-testid="eway-row" className="whitespace-nowrap">
                  <TableCell><Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid="eway-select-row" /></TableCell>
                  <TableCell className="text-sm">{ewayOffset + i + 1}</TableCell>
                  <TableCell className="text-sm font-medium">{r.eway_bill}</TableCell>
                  <TableCell className="text-sm">{r.lr_no || r.consignment_no || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="eway-lrdate-cell">{r.lr_date ? fmtDate(r.lr_date) : "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="eway-customer-cell">{r._customer || "—"}</TableCell>
                  <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="eway-vehicle-no">
                    {r.vehicle_no ? (
                      r.vehicle_no
                    ) : !r._delivered ? (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-700" data-testid="eway-no-vehicle-flag">No Vehicle</span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-sm" data-testid="eway-vehicle-type">{r.vehicle_type || "—"}</TableCell>
                  <TableCell className="text-sm">{r._validUpto ? fmtDate(r._validUpto) : "—"}</TableCell>
                  <TableCell className={`text-sm font-semibold ${r._expired ? "text-red-600" : "text-slate-400"}`}>
                    {r._expired ? fmtDate(r._autoExtended) : "—"}
                  </TableCell>
                  <TableCell className="text-sm">{r._delivered ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    {r._delivered ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Delivered · closed</span>
                    ) : r._expired ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid="eway-needs-extension">
                          <AlertTriangle className="h-3 w-3" /> Needs extension
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 border-emerald-300 px-2 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
                          title={`Extended on GST portal — roll valid date to ${fmtDate(r._autoExtended)}`}
                          onClick={async () => {
                            try {
                              await api.post("/eway/extend", { shipment_id: r._sid, eway_no: r.eway_bill, valid: r._autoExtended });
                              toast.success(`EWB ${r.eway_bill} valid date rolled to ${fmtDate(r._autoExtended)}`);
                              api.get("/shipments").then((res) => setRows(res.data)).catch(() => {});
                            } catch (e) {
                              toast.error(formatApiError(e.response?.data?.detail) || "Failed to extend");
                            }
                          }}
                          data-testid="eway-mark-extended-btn"
                        >
                          Extended ✓
                        </Button>
                      </span>
                    ) : (
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">Active</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={data.length} page={pageNo} setPage={setPageNo} pageSize={EWAY_PER_PAGE} noun="e-way bills" testPrefix="eway" />
    </div>
  );
}
